import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CONDIZIONI_PROFESSIONALI } from '../../utils/inquadramento';
import AnteprimaDocumento, { haAnteprima } from '../../components/AnteprimaDocumento';
import { TIPI_DOCUMENTO, etichettaTipo, nomeDownload, ACCEPT, MAX_MB } from '../../utils/documentiDipendente';
import { ArrowLeft, Save, Check, AlertTriangle, Clock, UserX, UserCheck, ExternalLink, FileText, Download, Trash2, Eye, Pencil, Link, Unlink, Mail } from 'lucide-react';
import { SelectMansione, templatePertinente, tipoTemplateOk } from '../../utils/mansioni';
import api from '../../api/client';
import GestisciTessereStaff from '../../components/GestisciTessereStaff';
import RicalcoloTariffaModal from '../../components/RicalcoloTariffaModal';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import { MODALITA_COMPENSO, descriviCompenso, haFisso } from '../../utils/compenso';

const MESI = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const SOGLIA_ESENTE = 5000;
const SOGLIA_INPS   = 15000;

const fasciaInfo = (fascia) => ({
  ESENTE: { color:'#22C55E', bg:'#22C55E15', desc:'Nessun obbligo fiscale o contributivo' },
  INPS:   { color:'#F59E0B', bg:'#F59E0B15', desc:'Esente IRPEF · soggetto a INPS Gestione Separata' },
  IRPEF:  { color:'#EF4444', bg:'#EF444415', desc:'IRPEF + INPS regime pieno' },
}[fascia] || { color:'#888', bg:'#88888815', desc:'' });

const statoInfo = (stato) => ({
  INSERITA:     { color:'#888',    label:'Inserita'     },
  CONFERMATA:   { color:'#22C55E', label:'Confermata'   },
  SEGNALATA:    { color:'#EF4444', label:'Segnalata'    },
  DA_APPROVARE: { color:'#F59E0B', label:'Da approvare' },
  RIFIUTATA:    { color:'#EF4444', label:'Rifiutata'    },
  SOSTITUITA:   { color:'#555',    label:'Sostituita'   },
}[stato] || { color:'#888', label:stato });

const statoContrattoInfo = (stato) => ({
  GENERATO: { color:'#888',    label:'Generato' },
  INVIATO:  { color:'#F59E0B', label:'Inviato'  },
  FIRMATO:  { color:'#22C55E', label:'Firmato'  },
}[stato] || { color:'#888', label:stato });


export default function SchedaDipendente() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const puoCreare    = puoFare(user, 'dipendenti', 'create');
  const puoEditare   = puoFare(user, 'dipendenti', 'edit');
  // IBAN, inquadramento fiscale e documenti: permesso separato dati_paghe (GDPR, il server non li manda senza)
  const puoVedereDatiPaghe = puoFare(user, 'dati_paghe', 'view');
  const puoGestireDocumenti = puoFare(user, 'dati_paghe', 'edit'); // carica/elimina documenti (stesso permesso del server)
  const puoEliminare = puoFare(user, 'dipendenti', 'delete');
  const puoEliminarePresenza = puoFare(user, 'presenze', 'delete');
  const anno = new Date().getFullYear();

  const [dipendente, setDipendente]   = useState(null);
  const [tab, setTab]                 = useState('anagrafica');
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [form, setForm]               = useState({});
  const [presenze, setPresenze]       = useState([]);
  const [filtroMese, setFiltroMese]   = useState(new Date().getMonth() + 1);
  const [filtroAnno, setFiltroAnno]   = useState(anno);
  const [templates, setTemplates]     = useState([]);
  const [contratti, setContratti]     = useState([]);
  const [autocertificazioni, setAutocertificazioni] = useState([]);
  const [generando, setGenerando]     = useState(false);
  const [inviandoId, setInviandoId]   = useState(null);
  const [templateSel, setTemplateSel] = useState('');
  const [annoContratto, setAnnoContratto] = useState(anno);
  const [utentiDisp, setUtentiDisp]   = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [collegando, setCollegando]   = useState(false);
  const [mostraTuttiTemplate, setMostraTuttiTemplate] = useState(false);
  const [documenti, setDocumenti]     = useState([]);
  const [anteprimaDoc, setAnteprimaDoc] = useState(null);
  const [docForm, setDocForm]         = useState({ tipo: 'CUD', nome: '', anno: '', fileData: '', fileName: '' });
  const [caricandoDoc, setCaricandoDoc] = useState(false);
  const [cambioTariffa, setCambioTariffa] = useState(null); // { prima, dopo } dopo un salvataggio con tariffa oraria cambiata
  const [costoAnno, setCostoAnno]     = useState(null); // contributi e costo società dell'anno (permesso dati_paghe)

  useEffect(() => {
    if (!puoVedereDatiPaghe) return;
    api.get(`/paghe/anno/${id}/${anno}`).then(r => setCostoAnno(r.data)).catch(() => setCostoAnno(null));
  }, [id, anno, puoVedereDatiPaghe]);

  const loadDipendente = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dipendenti/${id}?anno=${anno}`);
      setDipendente(r.data);
      setForm({
        nome:                r.data.nome,
        cognome:             r.data.cognome,
        email:               r.data.email || '',
        telefono:            r.data.telefono || '',
        codice_fiscale:      r.data.codice_fiscale || '',
        data_nascita:        r.data.data_nascita ? r.data.data_nascita.slice(0,10) : '',
        luogo_nascita:       r.data.luogo_nascita || '',
        provincia_nascita:   r.data.provincia_nascita || '',
        indirizzo_residenza: r.data.indirizzo_residenza || '',
        cap:                 r.data.cap || '',
        paese:               r.data.paese || 'Italia',
        provincia:           r.data.provincia || '',
        ruolo_descrizione:   r.data.ruolo_descrizione || '',
        ruolo_specifica:     r.data.ruolo_specifica || '',
        modalita_compenso:   r.data.modalita_compenso || 'ORARIO',
        tariffa_oraria:      r.data.tariffa_oraria,
        compenso_fisso:      r.data.compenso_fisso ?? '',
        data_inizio:         r.data.data_inizio ? r.data.data_inizio.slice(0,10) : '',
        compenso_extra_importo: r.data.compenso_extra_importo ?? '',
        compenso_extra_anno:    r.data.compenso_extra_anno ?? '',
        compenso_pregresso_importo: r.data.compenso_pregresso_importo ?? '',
        compenso_pregresso_anno:    r.data.compenso_pregresso_anno ?? '',
        dipendente_pubblico: !!r.data.dipendente_pubblico,
        fuori_controllo_planning: !!r.data.fuori_controllo_planning,
        condizione_professionale: r.data.condizione_professionale || '',
        altra_previdenza:    r.data.altra_previdenza ?? null,
        richiede_detrazioni: !!r.data.richiede_detrazioni,
        note_fiscali:        r.data.note_fiscali || '',
        iban:                r.data.iban ? r.data.iban.replace(/(.{4})/g, '$1 ').trim() : '',
        banca:               r.data.banca || '',
        note:                r.data.note || '',
      });
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [id, anno]);

  const loadPresenze = useCallback(async () => {
    try {
      const r = await api.get(`/dipendenti/${id}/presenze?anno=${filtroAnno}&mese=${filtroMese}`);
      setPresenze(r.data);
    } catch(e) { console.error(e); }
  }, [id, filtroAnno, filtroMese]);

  const loadContratti = useCallback(async () => {
    try {
      const [rc, rt, ra] = await Promise.all([
        api.get(`/dipendenti/${id}/contratti`),
        api.get('/dipendenti/contratti/template'),
        api.get(`/dipendenti/${id}/autocertificazioni`),
      ]);
      setContratti(rc.data);
      setTemplates(rt.data);
      setAutocertificazioni(ra.data);
      if (rt.data.length > 0 && !templateSel) {
        const modalita = dipendente?.modalita_compenso || 'ORARIO';
        const match = rt.data.find(t => templatePertinente(t, dipendente) && t.ruoli?.length) || rt.data.find(t => templatePertinente(t, dipendente)) || rt.data.find(t => tipoTemplateOk(t, modalita)) || rt.data[0];
        setTemplateSel(String(match.id));
      }
    } catch(e) { console.error(e); }
  }, [id, templateSel, dipendente]);

  const loadDocumenti = useCallback(async () => {
    try { const r = await api.get(`/dipendenti/${id}/documenti`); setDocumenti(r.data); }
    catch(e) { console.error(e); }
  }, [id]);

  const handleDocFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) { alert(`File troppo grande (massimo ${MAX_MB} MB).`); e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(',')[1];
      setDocForm(f => ({ ...f, fileData: base64, fileName: file.name, nome: f.nome || file.name }));
    };
    reader.readAsDataURL(file);
  };

  const uploadDocumento = async () => {
    if (!docForm.fileData) return alert('Seleziona un file.');
    setCaricandoDoc(true);
    try {
      await api.post(`/dipendenti/${id}/documenti`, { ...docForm, anno: docForm.anno ? parseInt(docForm.anno) : null });
      setDocForm({ tipo: 'CUD', nome: '', anno: '', fileData: '', fileName: '' });
      loadDocumenti();
    } catch(e) { alert('Errore caricamento: ' + (e.response?.data?.error || e.message)); }
    finally { setCaricandoDoc(false); }
  };

  const eliminaDocumento = async (docId, nome) => {
    if (!window.confirm(`Eliminare il documento "${nome}"?`)) return;
    try { await api.delete(`/dipendenti/documenti/${docId}`); loadDocumenti(); }
    catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const scaricaDocumento = async (docId, nome) => {
    try {
      const r = await api.get(`/dipendenti/documenti/${docId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = nome; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('Errore download: ' + (e.response?.data?.error || e.message)); }
  };

  const scaricaAutocertificazioneStaff = async (aId, label) => {
    try {
      const r = await api.get(`/dipendenti/autocertificazioni/${aId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${label}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('Errore download: ' + (e.response?.data?.error || e.message)); }
  };

  const loadUtentiDisp = useCallback(async () => {
    try {
      const r = await api.get('/users');
      // Qualsiasi utente attivo è collegabile: un socio diventa allenatore, uno staff (ADMIN/OPERATORE/ROOT) mantiene il suo ruolo e diventa "anche" allenatore.
      setUtentiDisp(r.data.filter(u => u.attivo));
    } catch(e) {}
  }, []);

  useEffect(() => { loadDipendente(); loadUtentiDisp(); }, [loadDipendente, loadUtentiDisp]);
  useEffect(() => { if (tab === 'presenze' && puoFare(user,'presenze','view')) loadPresenze(); }, [tab, loadPresenze, user]);
  useEffect(() => { if (tab === 'contratti') loadContratti(); }, [tab, loadContratti]);
  useEffect(() => { if (tab === 'documenti') loadDocumenti(); }, [tab, loadDocumenti]);

  const generaContratto = async () => {
    if (!templateSel) return alert('Seleziona un template.');
    setGenerando(true);
    try {
      const r = await api.post(`/dipendenti/${id}/contratti/genera`,
        { template_id: parseInt(templateSel), anno: annoContratto },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contratto_${dipendente.cognome}_${dipendente.nome}_${annoContratto}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      loadContratti();
    } catch(e) { alert('Errore generazione: ' + (e.response?.data?.error || e.message)); }
    finally { setGenerando(false); }
  };

  const scaricaContrattoGenerato = async (cId, fileName) => {
    try {
      const r = await api.get(`/dipendenti/contratti/generati/${cId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('Errore download: ' + (e.response?.data?.error || e.message)); }
  };

  const eliminaContrattoGenerato = async (cId, label) => {
    if (!window.confirm(`Eliminare il contratto "${label}"?`)) return;
    try {
      await api.delete(`/dipendenti/contratti/generati/${cId}`);
      loadContratti();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const rinominaContrattoGenerato = async (cId, nomeAttuale) => {
    const nuovo = window.prompt('Rinomina contratto:', nomeAttuale || '');
    if (nuovo === null || nuovo.trim() === '') return;
    try {
      await api.patch(`/dipendenti/contratti/generati/${cId}/nome`, { nome: nuovo.trim() });
      loadContratti();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const inviaContrattoGenerato = async (cId, label) => {
    if (!dipendente?.email) return alert('Il dipendente non ha un indirizzo email in anagrafica: aggiungilo nella tab Anagrafica prima di inviare.');
    if (!window.confirm(`Inviare via email il contratto "${label}" a ${dipendente.email}?`)) return;
    setInviandoId(cId);
    try {
      await api.post(`/dipendenti/contratti/generati/${cId}/invia`);
      loadContratti();
    } catch(e) { alert('Errore invio: ' + (e.response?.data?.error || e.message)); }
    finally { setInviandoId(null); }
  };

  const collegaUtente = async () => {
    if (!selectedUserId) return;
    setCollegando(true);
    try {
      await api.patch(`/dipendenti/${id}/collega-utente`, { user_id: parseInt(selectedUserId) });
      setSelectedUserId('');
      await Promise.all([loadDipendente(), loadUtentiDisp()]);
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setCollegando(false); }
  };

  const scollegaUtente = async () => {
    if (!window.confirm('Scollegare l\'account utente da questo dipendente? Il ruolo tornerà a UTENTE.')) return;
    setCollegando(true);
    try {
      await api.patch(`/dipendenti/${id}/collega-utente`, { user_id: null });
      await Promise.all([loadDipendente(), loadUtentiDisp()]);
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setCollegando(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.compenso_extra_importo && !form.compenso_extra_anno) {
      return alert('Hai inserito un "compenso già percepito altrove" ma manca l\'anno di riferimento: senza quello non verrà conteggiato nella soglia fiscale.');
    }
    if (form.compenso_pregresso_importo && !form.compenso_pregresso_anno) {
      return alert('Hai inserito i "compensi periodo gennaio–agosto" ma manca l\'anno: senza quello non verranno conteggiati nella soglia fiscale.');
    }
    setSaving(true);
    try {
      await api.put(`/dipendenti/${id}`, {
        ...form,
        tariffa_oraria: parseFloat(form.tariffa_oraria) || 0,
        compenso_fisso: form.compenso_fisso !== '' ? parseFloat(form.compenso_fisso) : null,
        data_inizio: form.data_inizio || null,
        compenso_extra_importo: form.compenso_extra_importo !== '' ? parseFloat(form.compenso_extra_importo) : null,
        compenso_extra_anno:    form.compenso_extra_anno    !== '' ? parseInt(form.compenso_extra_anno, 10) : null,
        compenso_pregresso_importo: form.compenso_pregresso_importo !== '' ? parseFloat(form.compenso_pregresso_importo) : null,
        compenso_pregresso_anno:    form.compenso_pregresso_anno    !== '' ? parseInt(form.compenso_pregresso_anno, 10) : null,
      });
      loadDipendente();
      // Le presenze già inserite tengono l'importo calcolato alla creazione: se è cambiata la tariffa
      // oraria si chiede da che mese vale, per ricalcolarle.
      const tariffaPrima = dipendente.tariffa_oraria || 0;
      const tariffaDopo  = parseFloat(form.tariffa_oraria) || 0;
      const orario = dipendente.modalita_compenso !== 'FISSO' && form.modalita_compenso !== 'FISSO';
      if (orario && tariffaDopo !== tariffaPrima) {
        if (puoFare(user,'presenze','edit')) setCambioTariffa({ prima: tariffaPrima, dopo: tariffaDopo });
        else alert('Salvato. Attenzione: le presenze già inserite restano alla tariffa precedente — per ricalcolarle serve il permesso di modifica presenze.');
      } else alert('Salvato.');
    } catch(e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const disabilita = async () => {
    if (!window.confirm(`Disabilitare ${dipendente.nome} ${dipendente.cognome}? Lo storico verrà mantenuto.`)) return;
    try {
      await api.delete(`/dipendenti/${id}`);
      navigate('/dipendenti');
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const riabilita = async () => {
    try {
      await api.patch(`/dipendenti/${id}/riabilita`);
      loadDipendente();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const eliminaPresenza = async (presenzaId, stato) => {
    if (!window.confirm(stato === 'CONFERMATA' ? 'Questa presenza è già confermata: eliminandola le ore e il compenso spariscono dal mese. Procedere?' : 'Eliminare questa presenza?')) return;
    try {
      await api.delete(`/dipendenti/presenze/${presenzaId}`);
      loadPresenze();
    } catch(e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    }
  };

  if (loading) return <div style={{ padding:'40px', color:'#555' }}>Caricamento...</div>;
  if (!dipendente) return <div style={{ padding:'40px', color:'#EF4444' }}>Dipendente non trovato.</div>;

  const kpi    = dipendente.kpi || {};
  const fi     = fasciaInfo(kpi.fascia);
  const sogliaTarget = kpi.fascia === 'IRPEF' ? SOGLIA_INPS : kpi.fascia === 'INPS' ? SOGLIA_INPS : SOGLIA_ESENTE;
  const totaleSoglia = kpi.totale_ai_fini_soglia ?? kpi.totale_importo ?? 0;
  const pctBarra      = Math.min(100, (totaleSoglia / sogliaTarget) * 100);

  // Raggruppa presenze per giorno
  const presenzePerGiorno = presenze.reduce((g, p) => {
    const k = p.data.slice(0,10);
    if (!g[k]) g[k] = [];
    g[k].push(p);
    return g;
  }, {});

  // Totali mese
  const totOre    = presenze.reduce((s, p) => s + p.ore_lavorate, 0);
  const totImport = Math.round(presenze.reduce((s, p) => s + p.importo, 0)); // totale del mese arrotondato all'euro
  const daConf    = presenze.filter(p => p.stato === 'INSERITA').length;
  const segnalate = presenze.filter(p => p.stato === 'SEGNALATA').length;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'24px' }}>
        <button style={{ ...s.btnSec, padding:'8px' }} onClick={() => navigate('/dipendenti')}>
          <ArrowLeft size={15}/>
        </button>
        <div style={{ flex:1 }}>
          <h1 style={s.title}>{dipendente.cognome} {dipendente.nome}</h1>
           <p style={s.sub}>{dipendente.ruolo_descrizione}{dipendente.ruolo_specifica ? ` (${dipendente.ruolo_specifica})` : ''} · {descriviCompenso(dipendente)}{dipendente.fuori_controllo_planning ? ' · fuori controllo planning' : ''}</p>
        </div>
        <button style={s.btnSec} onClick={() => navigate('/dipendenti/calendario')}>
          <ExternalLink size={13}/> Calendario
        </button>
        {dipendente.attivo ? (
          puoEliminare && <button style={{ ...s.btnSec, color:'#EF4444', borderColor:'#EF444440' }} onClick={disabilita}>
            <UserX size={13}/> Disabilita
          </button>
        ) : (
          puoEditare && <button style={{ ...s.btnSec, color:'#22C55E', borderColor:'#22C55E40' }} onClick={riabilita}>
            <UserCheck size={13}/> Riabilita
          </button>
        )}
      </div>

      {/* Banner disabilitato */}
      {!dipendente.attivo && (
        <div style={{ background:'#EF444415', border:'1px solid #EF444440', borderRadius:'10px', padding:'10px 16px', marginBottom:'16px', fontSize: 'var(--font-size-xs)', color:'#EF4444', display:'flex', alignItems:'center', gap:'8px' }}>
          <UserX size={14}/> Questo dipendente è disabilitato. Lo storico presenze è mantenuto. Clicca "Riabilita" per riattivarlo.
        </div>
      )}

      {/* KPI fascia */}
      <div style={{ background:'var(--bg-card,#141414)', border:`1px solid ${fi.color}30`, borderRadius:'12px', padding:'16px 20px', marginBottom:'20px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'baseline', gap:'8px', marginBottom:'6px' }}>
              <span style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800' }}>
                €{(kpi.totale_importo || 0).toFixed(2)}
              </span>
              <span style={{ color:'#555', fontSize: 'var(--font-size-xs)' }}>dovuto da voi {anno}</span>
              <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'800', padding:'2px 8px', borderRadius:'4px', color:fi.color, background:fi.bg, marginLeft:'4px' }}>
                {kpi.fascia}
              </span>
            </div>
            <div style={{ height:'6px', background:'var(--bg-input)', borderRadius:'3px', overflow:'hidden', marginBottom:'6px' }}>
              <div style={{ width:`${pctBarra}%`, height:'100%', background:fi.color, borderRadius:'3px', transition:'width 0.4s' }}/>
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>
              {fi.desc}
              {kpi.fascia !== 'IRPEF' && <> · mancano <strong style={{ color:fi.color }}>€{(kpi.mancante_soglia || 0).toFixed(0)}</strong> alla soglia successiva</>}
            </div>
            {(kpi.extra_importo > 0 || kpi.pregresso_importo > 0) && (
              <div style={{ fontSize: 'var(--font-size-xs)', color:'#888', marginTop:'4px' }}>
                soglia fiscale cumulativa (DL 36/2021): <strong>€{totaleSoglia.toFixed(2)}</strong> = €{(kpi.totale_importo || 0).toFixed(2)} da voi nel gestionale
                {kpi.pregresso_importo > 0 && <> + €{kpi.pregresso_importo.toFixed(2)} da voi gennaio–agosto</>}
                {kpi.extra_importo > 0 && <> + €{kpi.extra_importo.toFixed(2)} da altre associazioni</>}
              </div>
            )}
          </div>
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <div style={{ fontSize: 'var(--font-size-xs)', color:'#555', marginBottom:'2px' }}>ORE TOTALI {anno}</div>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800' }}>
              {(kpi.totale_ore || 0).toFixed(2)}h
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>{kpi.n_presenze || 0} presenze</div>
          </div>
        </div>
        {costoAnno && costoAnno.mesi.length > 0 && (
          <div style={{ marginTop:'12px', paddingTop:'12px', borderTop:'1px solid var(--border,#1E1E1E)', display:'flex', gap:'24px', flexWrap:'wrap', fontSize:'var(--font-size-xs)', color:'#888' }}
            title={costoAnno.mesi.map(m => `${m.mese}/${anno}: lordo €${m.lordo.toFixed(2)}, società €${m.contributi_societa.toFixed(2)} (${m.fonte === 'CEDOLINO' ? 'cedolino' : 'stima'})`).join('\n')}>
            <div>COSTO SOCIETÀ {anno} <strong style={{ color:'var(--text,#fff)', fontSize:'var(--font-size-sm)' }}>€{costoAnno.totali.costo_societa.toFixed(2)}</strong></div>
            <div>di cui contributi INPS a vostro carico <strong>€{costoAnno.totali.contributi_societa.toFixed(2)}</strong></div>
            <div>trattenuti al collaboratore <strong>€{(costoAnno.totali.contributi_collab + costoAnno.totali.irpef).toFixed(2)}</strong></div>
            {costoAnno.mesi.some(m => m.fonte !== 'CEDOLINO' && m.contributi_societa > 0) && <div style={{ color:'#F59E0B' }}>contiene stime: inserisci i cedolini in Paghe mensili</div>}
          </div>
        )}
      </div>

      {/* Tab */}
      <div style={{ display:'flex', gap:'4px', marginBottom:'20px', borderBottom:'1px solid var(--border,#1E1E1E)' }}>
        {[['anagrafica','ANAGRAFICA'],...(puoFare(user,'presenze','view') ? [['presenze','PRESENZE']] : []),['contratti','CONTRATTI'],...(puoVedereDatiPaghe ? [['documenti','DOCUMENTI']] : []),...(puoFare(user,'tesseramenti','view') ? [['tesseramento','TESSERAMENTO']] : [])].map(([k,label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding:'10px 18px', background:'transparent', border:'none', borderBottom: tab===k?'2px solid #F4600C':'2px solid transparent',
              color: tab===k?'var(--text)':'#666', fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', cursor:'pointer', marginBottom:'-1px' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── TAB ANAGRAFICA ── */}
      {tab === 'anagrafica' && (
        <>
        <form onSubmit={handleSave}>
          {/* Sola lettura blindata: senza dipendenti:edit ogni campo e bottone del form è disabilitato, non solo il SALVA */}
          <fieldset disabled={!puoEditare} style={{ border:0, padding:0, margin:0, minWidth:0 }}>
          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px' }}>DATI GENERALI</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div><label style={s.label}>NOME *</label><input style={s.inp} value={form.nome} onChange={e=>setForm(p=>({...p,nome:e.target.value}))} required/></div>
            <div><label style={s.label}>COGNOME *</label><input style={s.inp} value={form.cognome} onChange={e=>setForm(p=>({...p,cognome:e.target.value}))} required/></div>
            <div><label style={s.label}>EMAIL</label><input style={s.inp} type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/></div>
            <div><label style={s.label}>TELEFONO</label><input style={s.inp} value={form.telefono} onChange={e=>setForm(p=>({...p,telefono:e.target.value}))}/></div>
            <div><label style={s.label}>MANSIONE (RIFORMA SPORT)</label>
              <SelectMansione style={s.inp} value={form.ruolo_descrizione} onChange={v=>setForm(p=>({...p,ruolo_descrizione:v}))}/>
            </div>
            <div><label style={s.label}>SPECIFICA (FACOLTATIVA)</label><input style={s.inp} placeholder="es. 2° grado beach volley" value={form.ruolo_specifica} onChange={e=>setForm(p=>({...p,ruolo_specifica:e.target.value}))}/></div>
            <div>
              <label style={s.label}>MODALITÀ COMPENSO</label>
              <div style={{ display:'flex', gap:'6px' }}>
                {MODALITA_COMPENSO.map(([v,label]) => (
                  <button key={v} type="button"
                    style={{ flex:1, padding:'8px 10px', borderRadius:'8px', border:`1px solid ${form.modalita_compenso===v?'#F4600C':'var(--border2,#2A2A2A)'}`,
                      background:form.modalita_compenso===v?'#F4600C20':'var(--bg-input,#1A1A1A)', color:form.modalita_compenso===v?'#F4600C':'#777',
                      fontWeight:'700', fontSize: 'var(--font-size-xs)', cursor:'pointer' }}
                    onClick={() => setForm(p=>({...p, modalita_compenso:v, tariffa_oraria: v==='FISSO' ? '0' : p.tariffa_oraria}))}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div><label style={s.label}>DATA INIZIO</label><input style={s.inp} type="date" value={form.data_inizio} onChange={e=>setForm(p=>({...p,data_inizio:e.target.value}))}/></div>
            {haFisso(form.modalita_compenso) && (
              <div><label style={s.label}>COMPENSO FISSO MENSILE (€) *</label><input style={s.inp} type="number" step="0.01" min="0" value={form.compenso_fisso ?? ''} onChange={e=>setForm(p=>({...p,compenso_fisso:e.target.value}))} required/></div>
            )}
            {form.modalita_compenso !== 'FISSO' && (
              <div><label style={s.label}>TARIFFA ORARIA (€) *</label><input style={s.inp} type="number" step="0.01" min={form.modalita_compenso==='FISSO_ORE' ? '0.01' : '0'} value={form.tariffa_oraria} onChange={e=>setForm(p=>({...p,tariffa_oraria:e.target.value}))} required/></div>
            )}
            {form.modalita_compenso === 'FISSO_ORE' && (
              <div style={{ gridColumn:'1/-1', fontSize:'var(--font-size-xs)', color:'var(--text-dim,#888)' }}>
                Ogni mese: fisso + tutte le ore inserite nelle presenze × tariffa oraria. Il fisso si propone da solo in Paghe mensili: apri il mese e salva.
              </div>
            )}
          </div>
          {user?.ruolo === 'ROOT' && (
            <label style={{ display:'flex', gap:'10px', alignItems:'flex-start', marginBottom:'16px', cursor:'pointer', fontSize:'var(--font-size-sm)' }}>
              <input type="checkbox" checked={!!form.fuori_controllo_planning} onChange={e=>setForm(p=>({...p,fuori_controllo_planning:e.target.checked}))} style={{ marginTop:'3px' }}/>
              <span><strong>Escludi dal controllo planning</strong>
                <span style={{ display:'block', color:'var(--text-dim,#888)', fontSize:'var(--font-size-xs)' }}>Per chi ha le lezioni incluse nel fisso e non segna le presenze: i suoi gruppi non compaiono più tra quelli "senza allenatore" e le sue ore non vengono controllate. Se un collega allena un suo gruppo, il collega viene comunque segnalato. Solo ROOT.</span>
              </span>
            </label>
          )}

          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px', marginTop:'4px' }}>DATI ANAGRAFICI</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div><label style={s.label}>CODICE FISCALE</label><input style={s.inp} value={form.codice_fiscale} onChange={e=>setForm(p=>({...p,codice_fiscale:e.target.value.toUpperCase()}))} maxLength={16}/></div>
            <div><label style={s.label}>DATA DI NASCITA</label><input style={s.inp} type="date" value={form.data_nascita} onChange={e=>setForm(p=>({...p,data_nascita:e.target.value}))}/></div>
            <div><label style={s.label}>LUOGO DI NASCITA</label><input style={s.inp} value={form.luogo_nascita} onChange={e=>setForm(p=>({...p,luogo_nascita:e.target.value}))}/></div>
            <div><label style={s.label}>PROVINCIA DI NASCITA</label><input style={s.inp} value={form.provincia_nascita} onChange={e=>setForm(p=>({...p,provincia_nascita:e.target.value.toUpperCase()}))} maxLength={2} placeholder="es. MI"/></div>
          </div>

          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px', marginTop:'4px' }}>RESIDENZA</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div style={{ gridColumn:'1/-1' }}><label style={s.label}>INDIRIZZO</label><input style={s.inp} value={form.indirizzo_residenza} onChange={e=>setForm(p=>({...p,indirizzo_residenza:e.target.value}))}/></div>
            <div><label style={s.label}>CAP</label><input style={s.inp} value={form.cap} onChange={e=>setForm(p=>({...p,cap:e.target.value}))} maxLength={5}/></div>
            <div><label style={s.label}>PROVINCIA</label><input style={s.inp} value={form.provincia} onChange={e=>setForm(p=>({...p,provincia:e.target.value.toUpperCase()}))} maxLength={2} placeholder="es. MI"/></div>
            <div style={{ gridColumn:'1/-1' }}><label style={s.label}>PAESE</label><input style={s.inp} value={form.paese} onChange={e=>setForm(p=>({...p,paese:e.target.value}))}/></div>
          </div>

          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px', marginTop:'4px' }}>SOGLIA FISCALE — COMPENSI PERIODO GENNAIO–AGOSTO</div>
          <p style={{ fontSize: 'var(--font-size-xs)', color:'#888', marginBottom:'12px', marginTop:'-4px' }}>
            Quanto gli avete già pagato voi nell'anno fiscale prima dell'uso del gestionale (2026: da gennaio ad agosto). Conta per le soglie e nell'autocertificazione come "già percepito qui", non come altre associazioni.
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div>
              <label style={s.label}>COMPENSI PERIODO GENNAIO–AGOSTO (€)</label>
              <input style={s.inp} type="number" step="0.01" min="0" value={form.compenso_pregresso_importo}
                onChange={e=>setForm(p=>({...p, compenso_pregresso_importo:e.target.value, compenso_pregresso_anno: (e.target.value && !p.compenso_pregresso_anno) ? String(anno) : p.compenso_pregresso_anno}))}/>
            </div>
            <div>
              <label style={s.label}>ANNO{!form.compenso_pregresso_anno && form.compenso_pregresso_importo && <span style={{ color:'#F59E0B', fontWeight:'400', textTransform:'none', letterSpacing:0 }}> — obbligatorio se inserisci un importo</span>}</label>
              <input style={s.inp} type="number" step="1" value={form.compenso_pregresso_anno} onChange={e=>setForm(p=>({...p,compenso_pregresso_anno:e.target.value}))}/>
            </div>
          </div>

          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px', marginTop:'4px' }}>SOGLIA FISCALE — COMPENSI DA ALTRE ASSOCIAZIONI</div>
          <p style={{ fontSize: 'var(--font-size-xs)', color:'#888', marginBottom:'12px', marginTop:'-4px' }}>
            Solo quanto percepito da ALTRE associazioni/società sportive nello stesso anno: la soglia ESENTE/INPS/IRPEF (cumulativa per persona, non per società) lo somma a quanto pagato da voi. Viene proposto all'allenatore nell'autocertificazione.
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div>
              <label style={s.label}>PERCEPITO DA ALTRE ASSOCIAZIONI (€)</label>
              <input style={s.inp} type="number" step="0.01" min="0" value={form.compenso_extra_importo}
                onChange={e=>setForm(p=>({...p, compenso_extra_importo:e.target.value, compenso_extra_anno: (e.target.value && !p.compenso_extra_anno) ? String(anno) : p.compenso_extra_anno}))}/>
            </div>
            <div>
              <label style={s.label}>ANNO DI RIFERIMENTO{!form.compenso_extra_anno && form.compenso_extra_importo && <span style={{ color:'#F59E0B', fontWeight:'400', textTransform:'none', letterSpacing:0 }}> — obbligatorio se inserisci un importo</span>}</label>
              <input style={s.inp} type="number" step="1" value={form.compenso_extra_anno} onChange={e=>setForm(p=>({...p,compenso_extra_anno:e.target.value}))}/>
            </div>
          </div>

          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px', marginTop:'4px' }}>DICHIARAZIONI PER IL CONTRATTO</div>
          <div style={{ marginBottom:'16px' }}>
            <label style={s.label}>DIPENDENTE DI UNA PUBBLICA AMMINISTRAZIONE</label>
            <div style={{ display:'flex', gap:'6px', maxWidth:'260px' }}>
              {[[false,'No'],[true,'Sì']].map(([v,label]) => (
                <button key={label} type="button"
                  style={{ flex:1, padding:'8px 10px', borderRadius:'8px', border:`1px solid ${form.dipendente_pubblico===v?'#F4600C':'var(--border2,#2A2A2A)'}`,
                    background:form.dipendente_pubblico===v?'#F4600C20':'var(--bg-input,#1A1A1A)', color:form.dipendente_pubblico===v?'#F4600C':'#777',
                    fontWeight:'700', fontSize: 'var(--font-size-xs)', cursor:'pointer' }}
                  onClick={() => setForm(p=>({...p,dipendente_pubblico:v}))}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {puoVedereDatiPaghe && (<>
          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px', marginTop:'4px' }}>DATI DI PAGAMENTO</div>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div>
              <label style={s.label}>IBAN</label>
              <input style={{ ...s.inp, fontFamily:'monospace', textTransform:'uppercase' }} value={form.iban} placeholder="IT00 X000 0000 0000 0000 0000 000"
                onChange={e=>setForm(p=>({...p,iban:e.target.value}))}/>
            </div>
            <div>
              <label style={s.label}>BANCA</label>
              <input style={s.inp} value={form.banca} onChange={e=>setForm(p=>({...p,banca:e.target.value}))}/>
            </div>
          </div>

          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px', marginTop:'4px' }}>INQUADRAMENTO PREVIDENZIALE E FISCALE (CO.CO.CO.)</div>
          <p style={{ fontSize: 'var(--font-size-xs)', color:'#888', marginBottom:'12px', marginTop:'-4px' }}>
            Dati informativi per l'elaboratore paghe: il gestionale non calcola ritenute né contributi.
            {dipendente.inquadramento_aggiornato_al && <> Ultimo aggiornamento: <b>{new Date(dipendente.inquadramento_aggiornato_al).toLocaleDateString('it-IT')}</b>.</>}
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
            <div>
              <label style={s.label}>CONDIZIONE PROFESSIONALE</label>
              <select style={s.inp} value={form.condizione_professionale}
                onChange={e=>{ const v = e.target.value; setForm(p=>({ ...p, condizione_professionale:v,
                  // suggerimento solo se non ancora indicato: resta modificabile a mano
                  altra_previdenza: p.altra_previdenza !== null ? p.altra_previdenza : (['DIPENDENTE','AUTONOMO','PENSIONATO'].includes(v) ? true : ['STUDENTE','NON_OCCUPATO'].includes(v) ? false : null) })); }}>
                <option value="">— Non indicata —</option>
                {CONDIZIONI_PROFESSIONALI.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>GIÀ COPERTO DA ALTRA PREVIDENZA OBBLIGATORIA / PENSIONATO</label>
              <div style={{ display:'flex', gap:'6px' }}>
                {[[false,'No'],[true,'Sì'],[null,'Da verificare']].map(([v,label]) => (
                  <button key={label} type="button"
                    style={{ flex:1, padding:'8px 10px', borderRadius:'8px', border:`1px solid ${form.altra_previdenza===v?'#F4600C':'var(--border2,#2A2A2A)'}`,
                      background:form.altra_previdenza===v?'#F4600C20':'var(--bg-input,#1A1A1A)', color:form.altra_previdenza===v?'#F4600C':'#777',
                      fontWeight:'700', fontSize: 'var(--font-size-xs)', cursor:'pointer' }}
                    onClick={() => setForm(p=>({...p,altra_previdenza:v}))}>
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ fontSize:'var(--font-size-xs)', color:'#888', marginTop:'4px' }}>
                {form.altra_previdenza === true ? 'Gestione Separata INPS: aliquota ridotta (24% nel 2026)' : form.altra_previdenza === false ? 'Gestione Separata INPS: aliquota piena (25% + aliquote aggiuntive nel 2026)' : 'Da chiarire con il collaboratore'}
              </div>
            </div>
          </div>
          <label style={{ display:'flex', alignItems:'flex-start', gap:'8px', marginBottom:'12px', fontSize:'var(--font-size-sm)', cursor:'pointer' }}>
            <input type="checkbox" checked={form.richiede_detrazioni} onChange={e=>setForm(p=>({...p,richiede_detrazioni:e.target.checked}))} style={{ accentColor:'#F4600C', marginTop:'3px' }}/>
            <span>Chiede le detrazioni da lavoro (art. 13 TUIR) sulla parte di compenso oltre €15.000
              <span style={{ display:'block', fontSize:'var(--font-size-xs)', color:'#888' }}>Non vanno chieste a due sostituti d'imposta insieme: se le applica già un altro datore di lavoro, lasciare deselezionato.</span>
            </span>
          </label>
          <div style={{ marginBottom:'16px' }}>
            <label style={s.label}>NOTE FISCALI (familiari a carico, altri redditi, ...)</label>
            <div style={{ fontSize:'var(--font-size-xs)', color:'#F59E0B', marginBottom:'6px' }}>Scrivi solo il necessario (es. "1 figlio a carico al 100%"): niente diagnosi o motivi di salute, sono dati particolarmente protetti.</div>
            <textarea style={{ ...s.inp, resize:'vertical', minHeight:'60px' }} value={form.note_fiscali} onChange={e=>setForm(p=>({...p,note_fiscali:e.target.value}))}/>
          </div>
          </>)}

          <div style={{ marginBottom:'16px' }}>
            <label style={s.label}>NOTE</label>
            <textarea style={{ ...s.inp, resize:'vertical', minHeight:'80px' }} value={form.note} onChange={e=>setForm(p=>({...p,note:e.target.value}))}/>
          </div>
          </fieldset>
          {puoEditare && (
            <div style={{ display:'flex', justifyContent:'flex-end' }}>
              <button type="submit" style={s.btnPri} disabled={saving}>
                <Save size={13}/> {saving ? 'SALVO...' : 'SALVA MODIFICHE'}
              </button>
            </div>
          )}
        </form>

        {/* Box accesso web allenatore */}
        <div style={{ marginTop:'20px', background:'var(--bg-card,#141414)', border:`1px solid ${dipendente.user_id ? '#A855F730' : 'var(--border2,#2A2A2A)'}`, borderRadius:'10px', padding:'16px' }}>
          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'12px', display:'flex', alignItems:'center', gap:'6px' }}>
            <Link size={11}/> ACCESSO WEB ALLENATORE
          </div>
          {dipendente.user ? (
            <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
              <div style={{ width:'36px', height:'36px', background:'#A855F720', border:'1px solid #A855F730', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <span style={{ color:'#A855F7', fontWeight:'800', fontSize: 'var(--font-size-sm)' }}>{dipendente.user.nome?.[0]}</span>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:'700', fontSize: 'var(--font-size-sm)' }}>{dipendente.user.nome}</div>
                <div style={{ color:'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>{dipendente.user.email}</div>
              </div>
              <span style={{ background:'#A855F720', color:'#A855F7', border:'1px solid #A855F740', borderRadius:'4px', padding:'2px 8px', fontSize: 'var(--font-size-xs)', fontWeight:'700' }}>
                {dipendente.user.ruolo}
              </span>
              {dipendente.user.is_allenatore && dipendente.user.ruolo !== 'ALLENATORE' && (
                <span style={{ background:'#22C55E20', color:'#22C55E', border:'1px solid #22C55E40', borderRadius:'4px', padding:'2px 8px', fontSize: 'var(--font-size-xs)', fontWeight:'700' }} title="Ha anche accesso al portale allenatore (presenze proprie)">
                  + ALLENATORE
                </span>
              )}
              <span style={{ color:'#22C55E', fontSize: 'var(--font-size-xs)', fontWeight:'700' }}>● COLLEGATO</span>
              {puoEditare && (
                <button style={{ background:'#EF444415', border:'1px solid #EF444430', borderRadius:'8px', padding:'6px 10px', color:'#EF4444', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', fontSize: 'var(--font-size-xs)', fontWeight:'700' }}
                  onClick={scollegaUtente} disabled={collegando}>
                  <Unlink size={12}/> SCOLLEGA
                </button>
              )}
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 'var(--font-size-xs)', color:'#888', marginBottom:'12px' }}>
                Nessun account collegato. Seleziona un utente per dargli accesso alle proprie presenze.
                Un socio (UTENTE) diventerà <span style={{ color:'#A855F7', fontWeight:'700' }}>ALLENATORE</span> con portale dedicato;
                uno staff già esistente (ADMIN/OPERATORE/ROOT) manterrà il proprio ruolo e vedrà in più "LE MIE PRESENZE" nel suo portale abituale.
              </p>
              {!puoEditare ? null : utentiDisp.length === 0 ? (
                <div style={{ fontSize: 'var(--font-size-xs)', color:'#555', fontStyle:'italic' }}>
                  Nessun socio/utente disponibile. Il socio deve prima registrarsi autonomamente.
                </div>
              ) : (
                <div style={{ display:'flex', gap:'8px', alignItems:'flex-end' }}>
                  <div style={{ flex:1 }}>
                    <label style={s.label}>SELEZIONA UTENTE</label>
                    <select style={s.inp} value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
                      <option value=''>— scegli un socio —</option>
                      {utentiDisp.map(u => (
                        <option key={u.id} value={u.id}>{u.nome} ({u.email}) · {u.ruolo}</option>
                      ))}
                    </select>
                  </div>
                  <button style={{ ...s.btnPri, opacity: selectedUserId ? 1 : 0.5 }}
                    onClick={collegaUtente} disabled={!selectedUserId || collegando}>
                    <Link size={13}/> {collegando ? 'COLLEGO...' : 'COLLEGA'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        </>
      )}

      {/* ── TAB PRESENZE ── */}
      {tab === 'presenze' && puoFare(user,'presenze','view') && (
        <div>
          {/* Filtri mese */}
          <div style={{ display:'flex', gap:'8px', marginBottom:'16px', alignItems:'center' }}>
            <select style={s.filterSel} value={filtroMese} onChange={e=>{setFiltroMese(+e.target.value);}}>
              {MESI.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
            <select style={s.filterSel} value={filtroAnno} onChange={e=>{setFiltroAnno(+e.target.value);}}>
              {[2025,2026,2027].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <button style={s.btnSec} onClick={loadPresenze}>Aggiorna</button>
            <div style={{ marginLeft:'auto', display:'flex', gap:'8px' }}>
              {daConf > 0 && <span style={{ fontSize: 'var(--font-size-xs)', color:'#888' }}>{daConf} da confermare</span>}
              {segnalate > 0 && <span style={{ fontSize: 'var(--font-size-xs)', color:'#EF4444' }}>{segnalate} segnalate</span>}
              <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', color:'#F4600C' }}>{totOre.toFixed(2)}h · €{totImport.toFixed(2)}</span>
            </div>
          </div>

          {presenze.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#555' }}>
              Nessuna presenza per {MESI[filtroMese-1]} {filtroAnno}.
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
              {Object.entries(presenzePerGiorno).sort(([a],[b]) => b.localeCompare(a)).map(([giorno, ps]) => (
                <div key={giorno} style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'10px', overflow:'hidden' }}>
                  <div style={{ padding:'8px 16px', background:'var(--bg-input,#1A1A1A)', fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', color:'#555' }}>
                    {new Date(giorno + 'T12:00:00').toLocaleDateString('it-IT',{ weekday:'long', day:'2-digit', month:'long' }).toUpperCase()}
                  </div>
                  {ps.map(p => {
                    const si = statoInfo(p.stato);
                    return (
                      <div key={p.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px', borderBottom:'1px solid var(--border,#1E1E1E)' }}>
                        <Clock size={13} color="#555" style={{ flexShrink:0 }}/>
                        <span style={{ fontFamily:'monospace', fontSize: 'var(--font-size-sm)', fontWeight:'600', minWidth:'110px' }}>
                          {p.ora_inizio} – {p.ora_fine}
                        </span>
                        <span style={{ fontSize: 'var(--font-size-xs)', color:'var(--text-muted,#888)', flex:1 }}>{p.nota || '—'}</span>
                        <span style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800' }}>
                          {p.ore_lavorate.toFixed(2)}h
                        </span>
                        <span style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color:'#F4600C', minWidth:'60px', textAlign:'right' }}>
                          €{p.importo.toFixed(2)}
                        </span>
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'800', padding:'2px 6px', borderRadius:'4px', color:si.color, background:si.color+'20', minWidth:'70px', textAlign:'center' }}>
                          {si.label}
                        </span>
                        {p.nota_allenatore && (
                          <span title={p.nota_allenatore}><AlertTriangle size={13} color="#EF4444"/></span>
                        )}
                        {puoEliminarePresenza && <button style={{ ...s.iconBtn, color:'#EF4444' }} onClick={() => eliminaPresenza(p.id, p.stato)}>✕</button>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB CONTRATTI ── */}
      {tab === 'contratti' && (
        <div>
          {/* Genera nuovo */}
          {puoCreare && <div style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'12px', padding:'16px', marginBottom:'20px' }}>
            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'12px' }}>GENERA CONTRATTO</div>
            {templates.length === 0 ? (
              <p style={{ fontSize: 'var(--font-size-xs)', color:'#888' }}>
                Nessun template disponibile. Carica un template .docx dalla sezione <strong style={{ color: 'var(--text)' }}>Dipendenti → Template Contratti</strong>.
              </p>
            ) : (
              <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
                <div style={{ flex:2, minWidth:'160px' }}>
                  <label style={s.label}>
                    TEMPLATE {dipendente.modalita_compenso === 'FISSO' ? '(compenso fisso)' : dipendente.modalita_compenso === 'FISSO_ORE' ? '(fisso + ore: a fisso o a ore)' : '(tariffa oraria)'}
                  </label>
                  {(() => {
                    const templatesFiltrati = templates.filter(t => templatePertinente(t, dipendente));
                    const elenco = mostraTuttiTemplate || templatesFiltrati.length === 0 ? templates : templatesFiltrati;
                    return (
                      <select style={{ ...s.inp }} value={templateSel} onChange={e=>setTemplateSel(e.target.value)}>
                        {elenco.map(t => <option key={t.id} value={t.id}>{t.nome} ({t.anno})</option>)}
                      </select>
                    );
                  })()}
                  {templates.some(t => !templatePertinente(t, dipendente)) && (
                    <button type="button" style={{ background:'transparent', border:'none', color:'#777', fontSize: 'var(--font-size-xs)', cursor:'pointer', padding:'4px 0', textDecoration:'underline' }}
                      onClick={() => setMostraTuttiTemplate(v => !v)}>
                      {mostraTuttiTemplate ? 'mostra solo i pertinenti' : 'mostra tutti i template'}
                    </button>
                  )}
                </div>
                <div style={{ flex:1, minWidth:'90px' }}>
                  <label style={s.label}>ANNO</label>
                  <input style={s.inp} type="number" value={annoContratto} onChange={e=>setAnnoContratto(+e.target.value)} min={2020} max={2040}/>
                </div>
                <button style={s.btnPri} onClick={generaContratto} disabled={generando}>
                  <FileText size={13}/> {generando ? 'GENERAZIONE...' : 'GENERA E SCARICA'}
                </button>
              </div>
            )}
          </div>}

          {/* Storico */}
          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px' }}>STORICO CONTRATTI GENERATI</div>
          {contratti.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#555', fontSize: 'var(--font-size-sm)' }}>Nessun contratto generato.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
              {contratti.map(c => {
                const label = c.nome || c.template?.nome || 'Contratto';
                const si = statoContrattoInfo(c.stato || 'GENERATO');
                return (
                  <div key={c.id} style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'10px', padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
                    <FileText size={15} color="#555"/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight:'700' }}>{label}</div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>
                        Anno {c.anno} · {new Date(c.created_at).toLocaleDateString('it-IT')}
                        {c.nome && c.template?.nome && <span> · <span style={{ color:'var(--text-dim)' }}>{c.template.nome}</span></span>}
                      </div>
                      <div style={{ marginTop:'4px', display:'flex', alignItems:'center', gap:'6px' }}>
                        <span style={{ fontSize:'10px', fontWeight:'700', letterSpacing:'0.5px', color:si.color, background:`${si.color}15`, padding:'2px 8px', borderRadius:'6px' }}>{si.label.toUpperCase()}</span>
                        {c.stato === 'INVIATO' && c.inviato_at && <span style={{ fontSize:'10px', color:'#555' }}>il {new Date(c.inviato_at).toLocaleDateString('it-IT')}</span>}
                        {c.stato === 'FIRMATO' && c.firmato_at && <span style={{ fontSize:'10px', color:'#555' }}>il {new Date(c.firmato_at).toLocaleDateString('it-IT')}</span>}
                      </div>
                    </div>
                    {puoEditare && <button style={s.iconBtn} title="Rinomina" onClick={() => rinominaContrattoGenerato(c.id, c.nome)}>
                      <Pencil size={13}/>
                    </button>}
                    {puoEditare && <button style={s.iconBtn} title={dipendente?.email ? `Invia via email a ${dipendente.email}` : 'Nessuna email in anagrafica'}
                      disabled={inviandoId === c.id} onClick={() => inviaContrattoGenerato(c.id, label)}>
                      <Mail size={13}/>
                    </button>}
                    <button style={s.btnSec} onClick={() => scaricaContrattoGenerato(c.id, c.file_nome)}>
                      <Download size={13}/> Scarica
                    </button>
                    {puoEliminare && <button style={{ ...s.btnSec, color:'#EF4444', borderColor:'#EF444430' }} title="Elimina" onClick={() => eliminaContrattoGenerato(c.id, label)}>
                      <Trash2 size={13}/>
                    </button>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Autocertificazioni compensi (art. 36 c.6-bis D.Lgs. 36/2021), rilasciate dall'allenatore stesso dal suo portale */}
          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px', marginTop:'28px' }}>STORICO AUTOCERTIFICAZIONI COMPENSI</div>
          {autocertificazioni.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#555', fontSize: 'var(--font-size-sm)' }}>Nessuna autocertificazione ancora ricevuta dall'allenatore.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
              {autocertificazioni.map(a => {
                const label = `Autocertificazione ${MESI[a.mese-1]} ${a.anno}`;
                return (
                  <div key={a.id} style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'10px', padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
                    <FileText size={15} color="#555"/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight:'700' }}>{label}</div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>
                        Totale dichiarato €{a.totale_dichiarato.toFixed(2)} (di cui €{a.importo_da_noi.toFixed(2)} da noi) · fascia {a.fascia} · dichiarata il {new Date(a.created_at).toLocaleDateString('it-IT')}
                      </div>
                    </div>
                    <button style={s.btnSec} onClick={() => scaricaAutocertificazioneStaff(a.id, label)}>
                      <Download size={13}/> Scarica
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TAB DOCUMENTI ── */}
      {tab === 'documenti' && puoVedereDatiPaghe && (
        <div>
          {puoGestireDocumenti && <div style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'12px', padding:'16px', marginBottom:'20px' }}>
            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'12px' }}>CARICA DOCUMENTO</div>
            <div style={{ display:'flex', gap:'8px', alignItems:'flex-end', flexWrap:'wrap' }}>
              <div style={{ flex:'0 0 190px' }}>
                <label style={s.label}>TIPO</label>
                <select style={s.inp} value={docForm.tipo} onChange={e=>setDocForm(f=>({...f,tipo:e.target.value}))}>
                  {Object.entries(TIPI_DOCUMENTO).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div style={{ flex:2, minWidth:'160px' }}>
                <label style={s.label}>NOME VISUALIZZATO</label>
                <input style={s.inp} value={docForm.nome} onChange={e=>setDocForm(f=>({...f,nome:e.target.value}))} placeholder="es. CUD 2025" />
              </div>
              <div style={{ flex:'0 0 90px' }}>
                <label style={s.label}>ANNO</label>
                <input style={s.inp} type="number" value={docForm.anno} onChange={e=>setDocForm(f=>({...f,anno:e.target.value}))} min={2020} max={2040} />
              </div>
              <div style={{ flex:'1 1 160px' }}>
                <label style={s.label}>FILE</label>
                <input style={{ ...s.inp, padding:'7px 0' }} type="file" accept={ACCEPT} onChange={handleDocFile} />
              </div>
              <button style={s.btnPri} onClick={uploadDocumento} disabled={caricandoDoc}>
                <FileText size={13}/> {caricandoDoc ? 'CARICAMENTO...' : 'CARICA'}
              </button>
            </div>
          </div>}

          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px' }}>DOCUMENTI CARICATI</div>
          {documenti.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#555', fontSize: 'var(--font-size-sm)' }}>Nessun documento caricato.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
              {documenti.map(d => (
                <div key={d.id} style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'10px', padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
                  <FileText size={15} color="#555"/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize: 'var(--font-size-sm)', fontWeight:'700', cursor: haAnteprima(d) ? 'pointer' : 'default' }} onClick={() => haAnteprima(d) && setAnteprimaDoc(d)}>
                      {d.nome}
                      {d.caricato_da_dipendente && <span style={{ marginLeft:'8px', fontSize:'10px', fontWeight:'700', letterSpacing:'1px', color:'#F4600C', background:'#F4600C1A', border:'1px solid #F4600C40', borderRadius:'5px', padding:'1px 6px' }}>CARICATO DAL COLLABORATORE</span>}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>
                      {etichettaTipo(d.tipo)}{d.anno ? ` · ${d.anno}` : ''} · {new Date(d.created_at).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                  {haAnteprima(d) && <button style={s.btnSec} onClick={() => setAnteprimaDoc(d)} title="Anteprima">
                    <Eye size={13}/> Vedi
                  </button>}
                  <button style={s.btnSec} onClick={() => scaricaDocumento(d.id, nomeDownload(d))} title="Scarica">
                    <Download size={13}/>
                  </button>
                  {puoGestireDocumenti && <button style={{ ...s.btnSec, color:'#EF4444', borderColor:'#EF444430' }} title="Elimina" onClick={() => eliminaDocumento(d.id, d.nome)}>
                    <Trash2 size={13}/>
                  </button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {cambioTariffa && <RicalcoloTariffaModal dipendenteId={id} tariffaPrima={cambioTariffa.prima} tariffaDopo={cambioTariffa.dopo}
        onClose={() => setCambioTariffa(null)}
        onApplicato={(r) => { setCambioTariffa(null); if (tab === 'presenze') loadPresenze(); alert(`Ricalcolate ${r.n_da_aggiornare} presenze (differenza ${r.differenza >= 0 ? '+' : ''}€${r.differenza.toFixed(2)}).`); }} />}

      {anteprimaDoc && <AnteprimaDocumento doc={anteprimaDoc} endpoint={`/dipendenti/documenti/${anteprimaDoc.id}/download`} onClose={() => setAnteprimaDoc(null)} />}

      {/* ── TAB TESSERAMENTO ── */}
      {tab === 'tesseramento' && puoFare(user,'tesseramenti','view') && (
        <div style={{ maxWidth:'480px' }}>
          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px' }}>TESSERAMENTO FEDERALE (ASI/FIPAV)</div>
          <GestisciTessereStaff personaId={dipendente.persona_id} />
        </div>
      )}
    </div>
  );
}

const s = {
  title:     { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  sub:       { color:'var(--text-muted,#888)', fontSize: 'var(--font-size-sm)', marginTop:'2px' },
  btnPri:    { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnSec:    { display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input,#1A1A1A)', color:'var(--text,#fff)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  label:     { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'#555', display:'block', marginBottom:'4px' },
  inp:       { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-sm)', outline:'none', width:'100%', boxSizing:'border-box' },
  filterSel: { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-xs)', outline:'none' },
  iconBtn:   { background:'transparent', border:'none', cursor:'pointer', padding:'4px', color:'var(--text-muted,#888)', display:'inline-flex', alignItems:'center' },
};
